import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Row,
  Col,
  Progress,
  Timeline,
  Badge,
  Alert as AntAlert,
  Button as AntButton,
  Dropdown,
  message as antdMessage,
  Popover,
} from "antd";
import type { MenuProps } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FlagOutlined,
  TagsOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  LockOutlined,
  UnlockOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  MoreOutlined,
  StopOutlined,
  PlayCircleOutlined,
  InboxOutlined,
  CheckOutlined,
  CloseOutlined,
  SendOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { projectsApis } from "../../../api/projects";
import Project3MSummary from "./Project3MSummary";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { useAccessControl } from "../../../utils/useAccessControl";
import { getColorByLabel } from "./ProjectInformationById";

// Status + flag chips shown next to the page title in the shell header.
export interface ProjectHeaderMeta {
  status?: string;
  lock?: boolean;
  isPrivate?: boolean;
  isStrict?: boolean;
  approvalRequired?: boolean;
}

interface ProjectQuickViewCardProps {
  projectId?: number | string;
  derivedProjectStatus?: string;
  onEdit?: () => void;
  onRefresh?: () => void;
  /** Reports status/lock/flag chips upward so the page header can show them */
  onProjectMeta?: (meta: ProjectHeaderMeta | null) => void;
}

export const getStatusColor = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "inprogress" || s === "in progress")
    return "processing";
  if (s === "completed" || s === "closed" || s === "approved") return "success";
  if (s === "onhold" || s === "on hold") return "warning";
  if (s === "cancelled" || s === "deleted" || s === "rejected") return "error";
  if (s === "draft" || s === "yettostart" || s === "yet to start")
    return "default";
  return "default";
};

const formatDate = (d: any) =>
  d ? dayjs(d).format("DD MMM YYYY") : "Not Set";

// Tinted value chips for the Project Details section — dot+tint construction
// per the design system (never solid antd Tags for values).
const CHIP_TONES = {
  blue: { bg: "#e5ebfa", color: "#1a4d8f" },
  green: { bg: "#f6ffed", color: "#237804" },
  amber: { bg: "#fff7e6", color: "#ad6800" },
  purple: { bg: "#f9f0ff", color: "#531dab" },
  red: { bg: "#fff1f0", color: "#a8071a" },
  teal: { bg: "#e6fffb", color: "#08979c" },
  grey: { bg: "#f0f2f5", color: "#526484" },
} as const;

const InfoChip: FC<{
  tone?: keyof typeof CHIP_TONES;
  children: React.ReactNode;
}> = ({ tone = "grey", children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: CHIP_TONES[tone].bg,
      color: CHIP_TONES[tone].color,
      borderRadius: 999,
      padding: "2px 10px",
      fontSize: 12.5,
      fontWeight: 500,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {children}
  </span>
);

// KPI tile styles — premium tokens (docs/pms-design-system.md)
const statTileStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e4e9f0",
  borderRadius: 12,
  padding: "12px 16px",
  height: "100%",
  boxShadow: "0 1px 2px rgba(2, 44, 87, 0.05), 0 1px 3px rgba(2, 44, 87, 0.04)",
};
const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#8094ae",
};
const statValueStyle: React.CSSProperties = {
  fontFamily: "Inter, Nunito, sans-serif",
  fontSize: 22,
  fontWeight: 700,
  color: "#022c57",
  fontVariantNumeric: "tabular-nums",
  lineHeight: 1.3,
  marginTop: 4,
};
const statSuffixStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "#8094ae",
};
const statMetaStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8094ae",
  marginTop: 2,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const ApprovalHierarchyPopover: FC<{ approvals: any[] }> = ({ approvals }) => (
  <Popover
    trigger={["hover", "click"]}
    placement="right"
    title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
    content={
      <Timeline className="approval-timeline">
        {approvals.map((approval: any, index: number) => {
          const isApproved = approval.status == "Approved";
          const isRejected = approval.status == "Rejected";
          return (
            <Timeline.Item
              key={index}
              dot={
                isApproved ? (
                  <CheckCircleOutlined style={{ color: "green" }} />
                ) : isRejected ? (
                  <CloseOutlined style={{ color: "red" }} />
                ) : undefined
              }
            >
              {approval?.userName}
              <div className="designation">{approval?.roleName}</div>
              {approval?.approvalStage && (
                <div className="designation">{approval?.approvalStage}</div>
              )}
              {approval?.actionDate && (
                <div className="designation">
                  {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                </div>
              )}
            </Timeline.Item>
          );
        })}
      </Timeline>
    }
  >
    <span
      style={{
        color: "#2557d6",
        textDecoration: "underline",
        cursor: "pointer",
      }}
    >
      Yes
    </span>
  </Popover>
);

const ProjectQuickViewCard: FC<ProjectQuickViewCardProps> = ({
  projectId,
  derivedProjectStatus,
  onEdit,
  onProjectMeta,
}) => {
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<any>(null);
  // Host element in the Tabs bar (projectDetailsView) that receives the
  // action buttons via portal, so they align with the tabs row.
  const [actionsHost, setActionsHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setActionsHost(document.getElementById("project-view-actions"));
  }, []);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { canEdit, canCancel, canApprove } = useAccessControl();
  const currentUserId = currentUser?.id;

  useEffect(() => {
    if (projectId) fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchProject = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const response: any = await projectsApis.getProjectById(
        projectId as any,
        derivedProjectStatus as any
      );
      if (response?.status && response?.data) {
        const p = response.data;
        setProject(p);
        onProjectMeta?.({
          status: p.derivedProjectStatus || p.projectStatus || p.status,
          lock: p.lock === true,
          isPrivate: !!p.isPrivate,
          isStrict: !!p.isStrict,
          approvalRequired: p.approvalRequired,
        });
      } else {
        setProject(null);
        onProjectMeta?.(null);
      }
    } catch (err) {
      console.error("Failed to fetch project:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApiResult = (response: ApiResponse | any) => {
    const { data, status, message, error } = response || {};
    if (status) {
      antdMessage.success(data?.message || "Action completed successfully");
      fetchProject();
      return true;
    }
    antdMessage.error(message || error || "Action failed");
    return false;
  };

  const runAction = async (
    fn: () => Promise<any>,
    failMessage = "Action failed"
  ) => {
    try {
      const response = await fn();
      handleApiResult(response);
    } catch (err) {
      console.error(err);
      antdMessage.error(failMessage);
    }
  };

  const deactivateProject = () =>
    runAction(
      () => projectsApis.deactivateProject(projectId as any),
      "Failed to deactivate project"
    );
  const activateProject = () =>
    runAction(
      () => projectsApis.activateProject(projectId as any),
      "Failed to activate project"
    );
  const lockProject = () =>
    runAction(
      () => projectsApis.lockProject(projectId as any),
      "Failed to lock project"
    );
  const unLockProject = () =>
    runAction(
      () => projectsApis.unLockProject(projectId as any),
      "Failed to unlock project"
    );
  const closeProject = () =>
    runAction(
      () => projectsApis.closeProject(projectId as any),
      "Failed to close project"
    );
  const archiveProject = () =>
    runAction(
      () => projectsApis.archiveProject(projectId as any),
      "Failed to archive project"
    );
  const requestForApproval = () =>
    runAction(
      () => projectsApis.requestForApproval(projectId as any),
      "Failed to request approval"
    );

  const approveProject = () => {
    Swal.fire({
      title: "Are you sure you want to approve?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, approve it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        runAction(
          () => projectsApis.approveProject(projectId as any),
          "Failed to approve project"
        );
      }
    });
  };

  const rejectProject = () => {
    Swal.fire({
      title: "Are you sure you want to reject?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reject it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { value: reason } = await Swal.fire({
          title: "Enter Reason for Rejection",
          input: "text",
          inputPlaceholder: "Type your reason here",
          showCancelButton: true,
          confirmButtonText: "Submit",
          cancelButtonText: "Cancel",
          inputValidator: (value) => {
            if (!value) return "You need to Enter Reason for rejection!";
            return null;
          },
        });
        if (reason) {
          runAction(
            () =>
              projectsApis.rejectProject({
                projectId: projectId as any,
                reason,
              }),
            "Failed to reject project"
          );
        }
      }
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
        No project data available
      </div>
    );
  }

  const status = project.derivedProjectStatus || project.projectStatus || project.status;
  const completionPercent = parseFloat(
    project.projectCompletionPercentage ||
      project.completionPercentage ||
      "0"
  );
  const totalTasks = project.totalTasks ?? project.taskCount ?? 0;
  const completedTasks = project.completedTasks ?? 0;
  const totalPhases = project.totalPhases ?? project.phaseCount ?? 0;
  const totalActivities =
    project.totalActivities ?? project.activityCount ?? 0;

  const plannedStart = project.plannedStartDate;
  const plannedEnd = project.plannedEndDate;
  const actualStart = project.actualStartDate;
  const actualEnd = project.actualEndDate;
  const mustFinishDate = project.mustFinishDate;

  const plannedDurationDays =
    plannedStart && plannedEnd
      ? Math.max(0, dayjs(plannedEnd).diff(dayjs(plannedStart), "day") + 1)
      : null;

  const daysUntilDue =
    plannedEnd && !actualEnd
      ? dayjs(plannedEnd).startOf("day").diff(dayjs().startOf("day"), "day")
      : null;
  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;

  const tags: any[] = project.projectTags || [];
  const boqs: any[] = project.projectBoqs || [];
  const rateCardName =
    project.projectRateCards?.[0]?.rateCardTitle ||
    project.rateCardTitle ||
    "N/A";

  const isLockedByOther =
    project.lock === true &&
    project.derivedProjectStatus !== "Draft" &&
    project.derivedProjectStatus !== "Pending Approval" &&
    project.lockedBy != currentUserId;

  const isInactive = project.derivedProjectStatus === "Inactive";

  // Action visibility flags (mirrors ProjectInformationById dropdown)
  const hasEditOption =
    project.lock === false ||
    (project.lock === true &&
      project.lockedBy === currentUserId &&
      project.derivedProjectStatus !== "Pending Approval");
  const hasCloseProject = project.phasesCompletionPercentage === 100;
  const hasRequestApproval =
    (project.approvalStatus === "Draft" ||
      project.approvalStatus === "Rejected") &&
    project.lockedBy === currentUserId;
  const hasArchive = project.derivedProjectStatus === "Closed";
  const isAdmin =
    (currentUser?.genericRole || "").toLowerCase() === "admin";
  const pendingApproval =
    project.derivedProjectStatus === "Pending Approval" &&
    (isAdmin ||
      project.projectApprovals?.find(
        (d: any) => d.userId === currentUserId && d.status !== "Approved"
      ));
  const hasDeactivate =
    project.derivedProjectStatus === "Active" && project.lock === false;
  const hasActivate = project.derivedProjectStatus === "Inactive";
  const hasLock = project.lock === false;
  const hasUnlock =
    project.lock === true &&
    project.lockedBy === currentUserId &&
    project.derivedProjectStatus !== "Draft" &&
    project.derivedProjectStatus !== "Pending Approval";

  const canEditModule = canEdit("Projects");
  const canCancelModule = canCancel("Projects");
  const canApproveModule = canApprove("Projects");

  const menuItems: MenuProps["items"] = [];
  if (hasCloseProject) {
    menuItems.push({
      key: "close",
      label: "Close Project",
      icon: <CloseOutlined />,
      onClick: () => closeProject(),
    });
  }
  if (hasRequestApproval && canEditModule) {
    menuItems.push({
      key: "request-approval",
      label: "Request For Approvals",
      icon: <SendOutlined />,
      onClick: () => requestForApproval(),
    });
  }
  if (hasArchive) {
    menuItems.push({
      key: "archive",
      label: "Archive",
      icon: <InboxOutlined />,
      onClick: () => archiveProject(),
    });
  }
  if (hasDeactivate && canCancelModule) {
    menuItems.push({
      key: "deactivate",
      label: "Deactivate",
      icon: <StopOutlined />,
      onClick: () => deactivateProject(),
    });
  }
  if (hasActivate && canEditModule) {
    menuItems.push({
      key: "activate",
      label: "Activate",
      icon: <PlayCircleOutlined />,
      onClick: () => activateProject(),
    });
  }
  if (hasLock && canEditModule) {
    menuItems.push({
      key: "lock",
      label: "Lock",
      icon: <LockOutlined />,
      onClick: () => lockProject(),
    });
  }
  if (hasUnlock && canEditModule) {
    menuItems.push({
      key: "unlock",
      label: "Unlock",
      icon: <UnlockOutlined />,
      onClick: () => unLockProject(),
    });
  }

  const showEditButton = hasEditOption && canEditModule && !!onEdit;

  // Action buttons — rendered into the Tabs bar (right side) via portal when
  // the host exists; falls back to an inline right-aligned row otherwise.
  const actionsNode =
    (pendingApproval && canApproveModule) ||
    showEditButton ||
    menuItems.length > 0 ? (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {pendingApproval && canApproveModule && (
          <>
            <AntButton
              size="small"
              style={{
                backgroundColor: "#d1f5ec",
                color: "#107154",
                borderColor: "#d1f5ec",
                fontWeight: 500,
              }}
              onClick={() => approveProject()}
            >
              Approve
            </AntButton>
            <AntButton
              size="small"
              style={{
                backgroundColor: "#fbe3e4",
                color: "#a94442",
                borderColor: "#fbe3e4",
                fontWeight: 500,
              }}
              onClick={() => rejectProject()}
            >
              Reject
            </AntButton>
          </>
        )}
        {showEditButton && (
          <AntButton
            type="primary"
            ghost
            icon={<EditOutlined />}
            onClick={() => onEdit?.()}
          >
            Edit
          </AntButton>
        )}
        {menuItems.length > 0 && (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <AntButton
              icon={<MoreOutlined style={{ fontSize: 16 }} />}
              style={{
                color: "#2557d6",
                borderColor: "#b3c4f1",
                background: "#f0f5fb",
              }}
            />
          </Dropdown>
        )}
      </div>
    ) : null;

  return (
    <div style={{ padding: "4px 0" }}>
      {actionsNode &&
        (actionsHost ? (
          createPortal(actionsNode, actionsHost)
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            {actionsNode}
          </div>
        ))}

      {/* Alerts */}
      {isInactive && (
        <AntAlert
          type="error"
          showIcon
          message={
            <span>
              <strong>Inactive:</strong> This Project is currently inactive.
              Last updated on{" "}
              {project.updatedAt
                ? dayjs(project.updatedAt).format("DD-MMM-YYYY")
                : "N/A"}{" "}
              by &quot;{project.updatedByUserName || "N/A"}&quot;.
            </span>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {isLockedByOther && (
        <AntAlert
          type="warning"
          showIcon
          icon={<LockOutlined />}
          message={
            <span>
              <strong>Locked:</strong> This Project is currently Locked. Last
              updated on{" "}
              {project.updatedAt
                ? dayjs(project.updatedAt).format("DD-MMM-YYYY")
                : "N/A"}{" "}
              by &quot;{project.lockedByUserName || "N/A"}&quot;.
            </span>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {/* KPI strip — aligned, equal-height stat tiles */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={8} xl={4}>
          <div style={statTileStyle}>
            <div style={eyebrowStyle}>Progress</div>
            <div style={{ marginTop: 6 }}>
              <Progress
                type="circle"
                percent={completionPercent}
                size={48}
                strokeColor="#2557d6"
                trailColor="#eef1f6"
                format={(percent) => (
                  <span
                    style={{
                      color: "#022c57",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {percent?.toFixed(0)}%
                  </span>
                )}
              />
            </div>
          </div>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <div style={statTileStyle}>
            <div style={eyebrowStyle}>Planned Duration</div>
            <div style={statValueStyle}>
              {plannedDurationDays ?? "—"}
              {plannedDurationDays != null && (
                <span style={statSuffixStyle}> days</span>
              )}
            </div>
            <div style={statMetaStyle}>
              {plannedStart && plannedEnd
                ? `${dayjs(plannedStart).format("DD MMM")} → ${dayjs(
                    plannedEnd
                  ).format("DD MMM YYYY")}`
                : "Dates not set"}
            </div>
          </div>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <div style={statTileStyle}>
            <div style={eyebrowStyle}>
              {isOverdue ? "Overdue By" : "Days Until Due"}
            </div>
            <div
              style={{
                ...statValueStyle,
                color: isOverdue ? "#e85347" : "#022c57",
              }}
            >
              {daysUntilDue == null ? "—" : Math.abs(daysUntilDue)}
              {daysUntilDue != null && (
                <span
                  style={{
                    ...statSuffixStyle,
                    color: isOverdue ? "#e85347" : "#8094ae",
                  }}
                >
                  {" "}
                  {Math.abs(daysUntilDue) === 1 ? "day" : "days"}
                </span>
              )}
            </div>
            <div style={statMetaStyle}>
              {plannedEnd ? `Due ${dayjs(plannedEnd).format("DD MMM YYYY")}` : " "}
            </div>
          </div>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <div style={statTileStyle}>
            <div style={eyebrowStyle}>Tasks Completed</div>
            <div style={statValueStyle}>
              {completedTasks}
              <span style={statSuffixStyle}> / {totalTasks}</span>
            </div>
            <div style={statMetaStyle}>
              {totalTasks > 0
                ? `${Math.round((completedTasks / totalTasks) * 100)}% of tasks`
                : " "}
            </div>
          </div>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <div style={statTileStyle}>
            <div style={eyebrowStyle}>Phases</div>
            <div style={statValueStyle}>{totalPhases}</div>
          </div>
        </Col>
        <Col xs={12} md={8} xl={4}>
          <div style={statTileStyle}>
            <div style={eyebrowStyle}>Activities</div>
            <div style={statValueStyle}>{totalActivities}</div>
          </div>
        </Col>
      </Row>

      {/* 3M summary — Materials / Manpower / Machinery with detail drawers */}
      <Project3MSummary projectId={projectId} />

      {/* Project Details */}
      <Card
        size="small"
        title={
          <span style={{ fontSize: 14, fontWeight: 600, color: "#022c57" }}>
            Project Details
          </span>
        }
        style={{ borderRadius: 12, marginBottom: 20 }}
        styles={{
          header: { borderBottom: "1px solid #e4e9f0", minHeight: 40 },
          body: { padding: "12px 16px" },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 1, md: 2 }}
          size="small"
          labelStyle={{
            fontWeight: 500,
            color: "#666",
            width: 160,
            verticalAlign: "top",
          }}
          contentStyle={{ wordBreak: "break-word" }}
        >
          <Descriptions.Item
            label={
              <>
                <UserOutlined style={{ marginRight: 6 }} />
                Owner
              </>
            }
          >
            <InfoChip tone={project.projectOwnerName ? "blue" : "grey"}>
              {project.projectOwnerName || "N/A"}
            </InfoChip>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <EnvironmentOutlined style={{ marginRight: 6 }} />
                Site
              </>
            }
          >
            <InfoChip tone={project.siteName ? "teal" : "grey"}>
              {project.siteName || "N/A"}
            </InfoChip>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Planned Start
              </>
            }
          >
            <InfoChip tone={plannedStart ? "blue" : "grey"}>
              {formatDate(plannedStart)}
            </InfoChip>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Planned End
              </>
            }
          >
            <InfoChip tone={plannedEnd ? "blue" : "grey"}>
              {formatDate(plannedEnd)}
            </InfoChip>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Actual Start
              </>
            }
          >
            <InfoChip tone={actualStart ? "green" : "grey"}>
              {actualStart ? dayjs(actualStart).format("DD MMM YYYY") : "N/A"}
            </InfoChip>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Actual End
              </>
            }
          >
            <InfoChip tone={actualEnd ? "green" : "grey"}>
              {actualEnd ? dayjs(actualEnd).format("DD MMM YYYY") : "N/A"}
            </InfoChip>
          </Descriptions.Item>
          {mustFinishDate ? (
            <Descriptions.Item
              label={
                <>
                  <CalendarOutlined style={{ marginRight: 6 }} />
                  Must Finish By
                </>
              }
            >
              <InfoChip tone="red">
                {dayjs(mustFinishDate).format("DD MMM YYYY")}
              </InfoChip>
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item
            label={
              <>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Calendar
              </>
            }
          >
            <InfoChip tone={project.calendarTitle ? "purple" : "grey"}>
              {project.calendarTitle || "N/A"}
            </InfoChip>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <>
                <DollarOutlined style={{ marginRight: 6 }} />
                Rate Card
              </>
            }
          >
            <InfoChip tone={rateCardName !== "N/A" ? "amber" : "grey"}>
              {rateCardName}
            </InfoChip>
          </Descriptions.Item>
          {project.budgetName ? (
            <Descriptions.Item
              label={
                <>
                  <DollarOutlined style={{ marginRight: 6 }} />
                  Budget
                </>
              }
            >
              <InfoChip tone="green">{project.budgetName}</InfoChip>
            </Descriptions.Item>
          ) : null}
          {project.projectGroupName ? (
            <Descriptions.Item
              label={
                <>
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  Project Group
                </>
              }
            >
              <InfoChip tone="purple">{project.projectGroupName}</InfoChip>
            </Descriptions.Item>
          ) : null}
          {project.approvalRequired !== undefined ? (
            <Descriptions.Item
              label={
                <>
                  <CheckCircleOutlined style={{ marginRight: 6 }} />
                  Approval Required
                </>
              }
            >
              {project.approvalRequired &&
              Array.isArray(project.projectApprovals) &&
              project.projectApprovals.length > 0 ? (
                <ApprovalHierarchyPopover
                  approvals={project.projectApprovals}
                />
              ) : (
                <Tag color={project.approvalRequired ? "blue" : "default"}>
                  {project.approvalRequired ? "Yes" : "No"}
                </Tag>
              )}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item
            label={
              <>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                Created
              </>
            }
          >
            <InfoChip tone="grey">
              {project.createdAt
                ? dayjs(project.createdAt).format("DD MMM YYYY")
                : "N/A"}
            </InfoChip>
            {project.createdByUserName ? (
              <span style={{ color: "#999", marginLeft: 8 }}>
                by {project.createdByUserName}
              </span>
            ) : null}
          </Descriptions.Item>
          {project.updatedAt && project.updatedAt !== project.createdAt ? (
            <Descriptions.Item
              label={
                <>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  Updated
                </>
              }
            >
              <InfoChip tone="grey">
                {dayjs(project.updatedAt).format("DD MMM YYYY")}
              </InfoChip>
              {project.updatedByUserName ? (
                <span style={{ color: "#999", marginLeft: 8 }}>
                  by {project.updatedByUserName}
                </span>
              ) : null}
            </Descriptions.Item>
          ) : null}
          {project.autoApprovalRemarks ? (
            <Descriptions.Item
              label={
                <>
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  Auto Approval Remarks
                </>
              }
              span={2}
            >
              {project.autoApprovalRemarks}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      {/* Description / Users / BOQ / Tags — aligned two-column grid */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {project.projectDescription && (
          <Col xs={24} lg={12}>
            <Card
              size="small"
              title={
                <span
                  style={{ fontSize: 14, fontWeight: 600, color: "#022c57" }}
                >
                  <FileTextOutlined style={{ marginRight: 6 }} />
                  Description
                </span>
              }
              style={{ borderRadius: 12, height: "100%" }}
              styles={{
                header: { borderBottom: "1px solid #e4e9f0", minHeight: 40 },
                body: { padding: "12px 16px" },
              }}
            >
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  color: "#262626",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {project.projectDescription}
              </div>
            </Card>
          </Col>
        )}

        {Array.isArray(project.projectUsers) &&
          project.projectUsers.length > 0 && (
            <Col xs={24} lg={12}>
              <Card
                size="small"
                title={
                  <span
                    style={{ fontSize: 14, fontWeight: 600, color: "#022c57" }}
                  >
                    <UserOutlined style={{ marginRight: 6 }} />
                    Project Users ({project.projectUsers.length})
                  </span>
                }
                style={{ borderRadius: 12, height: "100%" }}
                styles={{
                  header: { borderBottom: "1px solid #e4e9f0", minHeight: 40 },
                  body: { padding: "12px 16px" },
                }}
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {project.projectUsers.map((u: any, idx: number) => (
                    <Tag
                      key={u?.projectUserId ?? idx}
                      icon={<UserOutlined />}
                      color="blue"
                    >
                      {u?.userName ||
                        u?.fullName ||
                        `User ${u?.projectUserId}`}
                      {u?.projectUserType ? (
                        <span style={{ color: "#666", marginLeft: 4 }}>
                          ({u.projectUserType})
                        </span>
                      ) : null}
                    </Tag>
                  ))}
                </div>
              </Card>
            </Col>
          )}

        {boqs.length > 0 && (
          <Col xs={24} lg={12}>
            <Card
              size="small"
              title={
                <span
                  style={{ fontSize: 14, fontWeight: 600, color: "#022c57" }}
                >
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  Material BOQ
                </span>
              }
              style={{ borderRadius: 12, height: "100%" }}
              styles={{
                header: { borderBottom: "1px solid #e4e9f0", minHeight: 40 },
                body: { padding: "12px 16px" },
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {boqs.map((boq: any, index: number) => (
                  <Tag
                    key={boq?.projectBoqId ?? boq?.boqTargetId ?? index}
                    style={{
                      borderRadius: 4,
                      padding: "2px 10px",
                    }}
                  >
                    {boq?.boqTitle || "-"}
                  </Tag>
                ))}
              </div>
            </Card>
          </Col>
        )}

        {tags.length > 0 && (
          <Col xs={24} lg={12}>
            <Card
              size="small"
              title={
                <span
                  style={{ fontSize: 14, fontWeight: 600, color: "#022c57" }}
                >
                  <TagsOutlined style={{ marginRight: 8 }} />
                  Tags
                </span>
              }
              style={{ borderRadius: 12, height: "100%" }}
              styles={{
                header: { borderBottom: "1px solid #e4e9f0", minHeight: 40 },
                body: { padding: "12px 16px" },
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {tags.map((tag: any, index: number) => {
                  const color = getColorByLabel(tag?.tagName || "");
                  return (
                    <Tag
                      key={tag?.tagId ?? index}
                      style={{
                        backgroundColor: color,
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "2px 10px",
                      }}
                    >
                      {tag?.tagName || "-"}
                    </Tag>
                  );
                })}
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Lock Information */}
      {project.lock === true && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              <LockOutlined style={{ marginRight: 6 }} />
              Lock Information
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
          }}
          styles={{
            header: {
              borderBottom: "1px solid #ffe58f",
              minHeight: 40,
              background: "#fffbe6",
            },
            body: { padding: "12px 16px" },
          }}
        >
          <Descriptions
            column={1}
            size="small"
            labelStyle={{ fontWeight: 500, color: "#666", width: 140 }}
          >
            <Descriptions.Item label="Locked By">
              {project.lockedByUserName || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated">
              {project.updatedAt
                ? dayjs(project.updatedAt).format("DD MMM YYYY")
                : "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* On Hold Info */}
      {(status || "").toLowerCase() === "onhold" && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14, fontWeight: 600, color: "#d48806" }}>
              On Hold Information
            </span>
          }
          style={{
            borderRadius: 12,
            marginBottom: 20,
            background: "#fffbe6",
            border: "1px solid #ffe58f",
          }}
          styles={{
            header: {
              borderBottom: "1px solid #ffe58f",
              minHeight: 40,
              background: "#fffbe6",
            },
            body: { padding: "12px 16px" },
          }}
        >
          <Timeline
            items={[
              {
                color: "orange",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Hold Date</div>
                    <div style={{ color: "#666" }}>
                      {project.projectOnholdDate || project.onHoldDate
                        ? dayjs(
                            project.projectOnholdDate || project.onHoldDate
                          ).format("DD MMM YYYY")
                        : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "green",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Expected Resolution</div>
                    <div style={{ color: "#666" }}>
                      {project.projectETAResolution || project.etaResolution
                        ? dayjs(
                            project.projectETAResolution ||
                              project.etaResolution
                          ).format("DD MMM YYYY")
                        : "N/A"}
                    </div>
                  </>
                ),
              },
              {
                color: "blue",
                children: (
                  <>
                    <div style={{ fontWeight: 500 }}>Reason</div>
                    <div style={{ color: "#666" }}>
                      {project.projectOnholdReason ||
                        project.onHoldReason ||
                        "No reason provided"}
                    </div>
                  </>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default ProjectQuickViewCard;
